import React, { useEffect, useState } from 'react';
import { Button } from '../../components/ui';
import { SiteHeader } from '../../components/site/SiteHeader';
import { Hero } from '../../components/site/Hero';
import { HowItWorks } from '../../components/site/HowItWorks';
import { SpeedTimeline } from '../../components/site/SpeedTimeline';
import { Pricing } from '../../components/site/Pricing';
import { Faq } from '../../components/site/Faq';
import { SiteFooter } from '../../components/site/SiteFooter';

/** Scrolls to the hero and focuses its PLZ input — shared by the pricing "Free" CTA and the mobile sticky bar. */
function focusPlzInput() {
  const input = document.getElementById('hero-plz') as HTMLInputElement | null;
  const hero = document.getElementById('start');
  const reduceMotion = typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  hero?.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
  window.setTimeout(() => input?.focus(), reduceMotion ? 0 : 450);
}

const LandingPage: React.FC = () => {
  const [heroOutOfView, setHeroOutOfView] = useState(false);
  const [footerVisible, setFooterVisible] = useState(false);
  // QuickStart's step 2 has no `#hero-plz` input any more, so the sticky CTA (which
  // focuses it) can't do anything useful there — hide it instead (M8).
  const [quickStartStep, setQuickStartStep] = useState<1 | 2>(1);

  useEffect(() => {
    const hero = document.getElementById('start');
    if (!hero) return undefined;
    const observer = new IntersectionObserver(([entry]) => setHeroOutOfView(!entry.isIntersecting));
    observer.observe(hero);
    return () => observer.disconnect();
  }, []);

  // Hide the sticky CTA once the footer scrolls into view, so it never sits on top of
  // (and hides) the footer's own links/content (M8).
  useEffect(() => {
    const footer = document.getElementById('site-footer');
    if (!footer) return undefined;
    const observer = new IntersectionObserver(([entry]) => setFooterVisible(entry.isIntersecting));
    observer.observe(footer);
    return () => observer.disconnect();
  }, []);

  // Would the sticky CTA be relevant right now, ignoring whether the footer has already
  // taken it over? Used both to render the CTA (ANDed with `!footerVisible`) and to
  // reserve matching bottom padding on the footer so there's no overlap during the
  // scroll transition between the two.
  const stickyCtaRelevant = heroOutOfView && quickStartStep !== 2;
  const showStickyCta = stickyCtaRelevant && !footerVisible;

  return (
    <div id="landing-page-root">
      <SiteHeader />

      <main>
        <Hero onQuickStartStepChange={setQuickStartStep} />
        <HowItWorks />
        <SpeedTimeline />
        <Pricing onFreeSelect={focusPlzInput} />
        <Faq />
      </main>

      <SiteFooter
        className={stickyCtaRelevant ? 'pb-[calc(var(--landing-sticky-cta-h)+env(safe-area-inset-bottom))] lg:pb-0' : undefined}
      />

      {showStickyCta && (
        <div className="fixed inset-x-0 bottom-0 z-40 border-t border-line bg-card p-3 pb-[calc(env(safe-area-inset-bottom)+0.75rem)] shadow-card lg:hidden">
          <Button onClick={focusPlzInput} fullWidth>
            PLZ eingeben
          </Button>
        </div>
      )}
    </div>
  );
};

export default LandingPage;
